import { useEffect } from "react";
import { useSelector } from "react-redux";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getStore } from "../../storeRef";

//--------------------------------------------------------------------------------------
//  For redux service for applciation scoped (data wont change in application lifetime)
//--------------------------------------------------------------------------------------
export function createApplicationService({
  name,
  apiFetchFn,
  apiAddFn,
  apiUpdateFn,
  initialData = [],
}) {
  const fetchData = createAsyncThunk(`${name}/fetch`, async () => {
    const result = await apiFetchFn();
    return result.data.data;
  });

  const slice = createSlice({
    name,
    initialState: {
      data: initialData,
      fetched: false,
      loading: true,
    },
    reducers: {
      resetFetched(state) {
        state.fetched = false;
        state.loading = true;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchData.pending, (state) => {
          state.loading = true;
        })
        .addCase(fetchData.fulfilled, (state, action) => {
          state.data = action.payload;
          state.fetched = true;
          state.loading = false;
        })
        .addCase(fetchData.rejected, (state) => {
          state.loading = false;
        });
    },
  });

  const reducer = slice.reducer;

  let fetchPromise = null;

  const getData = async () => {
    const store = getStore();
    const state = store.getState();
    if (!state[name].fetched) {
      if (!fetchPromise) {
        fetchPromise = store.dispatch(fetchData()).finally(() => {
          fetchPromise = null;
        });
      }
      await fetchPromise;
    }
    return getStore().getState()[name].data;
  };

  const addData = async (data) => {
    try {
      await apiAddFn(data);
      getStore().dispatch(fetchData());
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const updateData = async (id, data) => {
    try {
      await apiUpdateFn(id, data);
      getStore().dispatch(fetchData());
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const useData = () => {
    const data = useSelector((state) => state[name].data);
    const fetched = useSelector((state) => state[name].fetched);
    const loading = useSelector((state) => state[name].loading);
    useEffect(() => {
      if (!fetched) {
        getData();
      }
    }, [fetched]);
    return { data, loading, fetched };
  };

  const resetData = () => {
    getStore().dispatch(slice.actions.resetFetched());
  };

  return {
    reducer,
    getData,
    addData,
    updateData,
    useData,
    resetData,
  };
}
